import { useState, useRef } from 'react';
// useRef kept for formRef

const AUDIT_OPTIONS = [
  { id: 'usfda', label: 'USFDA', description: 'U.S. Food & Drug Administration' },
  { id: 'health-canada', label: 'Health Canada', description: 'Health Canada GMP' },
  { id: 'edqm', label: 'EDQM', description: 'European Directorate for Quality of Medicines' },
  { id: 'mhra', label: 'MHRA', description: 'Medicines & Healthcare products Regulatory Agency' },
  { id: 'who', label: 'WHO', description: 'World Health Organization GMP' },
  { id: 'pics', label: 'PIC/S', description: 'Pharmaceutical Inspection Co-operation Scheme' },
  { id: 'other', label: 'Other', description: 'Other recognized regulatory body' },
];

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function SupplierApplicationForm() {
  const [selectedAudits, setSelectedAudits] = useState<string[]>([]);
  const [auditDropdownOpen, setAuditDropdownOpen] = useState(false);
  const [auditError, setAuditError] = useState(false);
  const [status, setStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);
  const [honeypot, setHoneypot] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const toggleAudit = (id: string) => {
    setSelectedAudits((prev) => {
      const next = prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id];
      if (next.length > 0) setAuditError(false);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'submitting') return;
    // Honeypot check — silently block bots
    if (honeypot) return;

    // Validate custom audit dropdown
    if (selectedAudits.length === 0) {
      setAuditError(true);
      const auditEl = document.getElementById('audit-dropdown');
      auditEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setAuditError(false);

    const form = e.currentTarget;
    const messageField = form.querySelector<HTMLTextAreaElement>('textarea[name="message"]');
    if (messageField && messageField.value.length > 500) return;

    // Normalize website URL — add https:// if missing
    const websiteEl = form.querySelector<HTMLInputElement>('[name="website"]');
    if (websiteEl && websiteEl.value && !websiteEl.value.match(/^https?:\/\//)) {
      websiteEl.value = `https://${websiteEl.value}`;
    }

    setStatus('submitting');

    const data = new URLSearchParams();
    const fields = ['company_name', 'contact_name', 'email', 'phone', 'country', 'product_categories', 'website', 'product_list_link', 'message'];
    fields.forEach((field) => {
      const el = form.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name="${field}"]`);
      if (el) data.append(field, el.value);
    });

    // Audits — selected labels
    const auditLabels = selectedAudits.map(
      (id) => AUDIT_OPTIONS.find((o) => o.id === id)?.label ?? id
    );
    data.append('regulatory_audits', auditLabels.join(', ') || 'None selected');

    try {
      const res = await fetch('https://readdy.ai/api/form/d7a249b86jhav3jpftng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        formRef.current?.reset();
        setSelectedAudits([]);
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const auditSummary =
    selectedAudits.length === 0
      ? 'Select all that apply...'
      : selectedAudits
          .map((id) => AUDIT_OPTIONS.find((o) => o.id === id)?.label)
          .join(', ');

  return (
    <section className="w-full px-6 md:px-16 py-16 bg-stone-50">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <span className="text-emerald-700 text-xs font-bold uppercase tracking-widest block mb-3">
              Partner With Us
            </span>
            <h2
              className="text-3xl md:text-4xl font-extrabold text-stone-900 leading-tight"
              style={{ fontFamily: "'DM Serif Display', serif" }}
            >
              Become a Supplier
            </h2>
            <p className="text-stone-500 text-sm mt-3 max-w-lg leading-relaxed">
              We partner with qualified, compliant, responsive manufacturers worldwide. Fill out the form below and our team will review your application — in English or French.
            </p>
          </div>

        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
          {[
            { icon: 'ri-file-text-line', step: '01', title: 'Submit Application', desc: 'Fill out your company details and upload your product list' },
            { icon: 'ri-search-eye-line', step: '02', title: 'Review & Qualification', desc: 'Our team reviews your regulatory compliance and product range' },
            { icon: 'ri-shake-hands-line', step: '03', title: 'Partnership Agreement', desc: 'Onboarding call and formal partnership agreement in FR or EN' },
          ].map(({ icon, step, title, desc }) => (
            <div key={step} className="bg-white rounded-lg p-5 border border-stone-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-emerald-50">
                  <i className={`${icon} text-emerald-700 text-base`} />
                </div>
                <span className="text-xs font-bold text-stone-300 tracking-widest">{step}</span>
              </div>
              <h4 className="text-sm font-bold text-stone-800 mb-1">{title}</h4>
              <p className="text-xs text-stone-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        {/* Form */}
        {status === 'success' ? (
          <div className="bg-white rounded-lg border border-emerald-200 p-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-100 mx-auto mb-5">
              <i className="ri-check-line text-emerald-700 text-3xl" />
            </div>
            <h3 className="text-xl font-bold text-stone-900 mb-2" style={{ fontFamily: "'DM Serif Display', serif" }}>
              Application Received
            </h3>
            <p className="text-stone-500 text-sm max-w-md mx-auto">
              Thank you for your interest. Our team will review your application and reach out within 3–5 business days — in your preferred language.
            </p>
            <button
              onClick={() => setStatus('idle')}
              className="mt-6 text-sm font-semibold text-emerald-700 hover:underline cursor-pointer"
            >
              Submit another application
            </button>
          </div>
        ) : (
          <form
            ref={formRef}
            data-readdy-form
            id="supplier-application-form"
            onSubmit={handleSubmit}
            className="bg-white rounded-lg border border-stone-100 p-8 md:p-10"
          >
            {/* Honeypot — invisible to real users */}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }} aria-hidden="true">
              <input
                type="text"
                name="full_name_confirm"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>
            <h3 className="text-base font-bold text-stone-800 mb-6 pb-4 border-b border-stone-100">
              Company Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                  Company Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="company_name"
                  required
                  placeholder="e.g. PharmaCorp Ltd."
                  className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                  Contact Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="contact_name"
                  required
                  placeholder="Full name"
                  className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                  Email Address <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="contact@company.com"
                  className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="+1 (555) 000-0000"
                  className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                  Country / Region <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="country"
                  required
                  placeholder="e.g. India, Germany, Canada"
                  className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                  Product Categories
                </label>
                <input
                  type="text"
                  name="product_categories"
                  placeholder="e.g. APIs, Excipients, Finished Dosage"
                  className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                  Company Website <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="website"
                  required
                  placeholder="https://www.yourcompany.com"
                  className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50"
                />
              </div>
            </div>

            {/* Regulatory Audits */}
            <div className="mb-5" id="audit-dropdown">
              <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                Facility Regulatory Audits <span className="text-red-400">*</span>
                <span className="text-stone-400 font-normal ml-1">(select all that apply)</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setAuditDropdownOpen((o) => !o)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 text-sm border rounded-md bg-stone-50 focus:outline-none cursor-pointer text-left transition-colors ${auditError ? 'border-red-400 focus:border-red-400' : 'border-stone-200 focus:border-emerald-400'}`}
                >
                  <span className={selectedAudits.length === 0 ? 'text-stone-400' : 'text-stone-800'}>
                    {auditSummary}
                  </span>
                  <i className={`ri-arrow-down-s-line text-stone-400 transition-transform ${auditDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {auditDropdownOpen && (
                  <div className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-md shadow-sm overflow-hidden">
                    {AUDIT_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => toggleAudit(opt.id)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-50 cursor-pointer transition-colors text-left border-b border-stone-50 last:border-0"
                      >
                        <div className={`w-4 h-4 flex items-center justify-center rounded border flex-shrink-0 transition-colors ${
                          selectedAudits.includes(opt.id)
                            ? 'bg-emerald-700 border-emerald-700'
                            : 'border-stone-300 bg-white'
                        }`}>
                          {selectedAudits.includes(opt.id) && (
                            <i className="ri-check-line text-white text-xs" />
                          )}
                        </div>
                        <div>
                          <span className="text-sm font-semibold text-stone-800">{opt.label}</span>
                          <span className="text-xs text-stone-400 ml-2">{opt.description}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {auditError && (
                <p className="text-xs text-red-500 mt-1.5">Please select at least one regulatory audit.</p>
              )}

              {selectedAudits.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedAudits.map((id) => {
                    const opt = AUDIT_OPTIONS.find((o) => o.id === id);
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 text-xs font-semibold bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full"
                      >
                        {opt?.label}
                        <button
                          type="button"
                          onClick={() => toggleAudit(id)}
                          className="cursor-pointer hover:text-emerald-900"
                        >
                          <i className="ri-close-line text-xs" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Product List Link */}
            <div className="mb-5">
              <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                Product List Link
                <span className="text-stone-400 font-normal ml-1">(Google Drive, Dropbox, WeTransfer, etc.)</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <i className="ri-links-line text-stone-400 text-sm" />
                </div>
                <input
                  type="text"
                  name="product_list_link"
                  placeholder="https://drive.google.com/..."
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50"
                />
              </div>
              <p className="text-xs text-stone-400 mt-1.5">
                Share a link to your product list hosted on any cloud storage platform. Make sure the link is set to &quot;Anyone with the link can view&quot;.
              </p>
            </div>

            {/* Message */}
            <div className="mb-8">
              <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                Additional Information
              </label>
              <textarea
                name="message"
                rows={4}
                maxLength={500}
                placeholder="Tell us about your manufacturing capabilities, capacity, key markets served, or any other relevant details..."
                onChange={(e) => setCharCount(e.target.value.length)}
                className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50 resize-none"
              />
              <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-red-400' : 'text-stone-400'}`}>
                {charCount}/500
              </p>
            </div>

            {status === 'error' && (
              <p className="text-sm text-red-500 mb-4">
                Something went wrong. Please try again or contact us directly.
              </p>
            )}

            {/* Patent Note */}
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
              <p className="text-xs text-amber-800 leading-relaxed">
                <span className="font-bold uppercase tracking-wide">Note:</span> We are not able to distribute products that are actively covered by process or product patents and it is the responsibility of the manufacturer to ensure that products do not infringe active patents in identified markets.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs text-stone-400 max-w-sm">
                We respond in English or French within 3–5 business days. Your information is kept strictly confidential.
              </p>
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="whitespace-nowrap bg-emerald-700 text-white text-sm font-bold px-8 py-3 rounded-md hover:bg-emerald-800 transition-colors cursor-pointer disabled:opacity-60"
              >
                {status === 'submitting' ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
